/**
 * Truncate HTML string and keep tag safe.
 *
 * @method truncate
 * @param {String} string string needs to be truncated
 * @param {Number} maxLength length of truncated string
 * @param {Object} options (optional)
 * @param {Boolean} [options.keepImageTag] flag to specify if keep image tag, false by default
 * @param {Boolean} [options.truncateLastWord] truncates last word, true by default
 * @param {Number} [options.slop] tolerance when options.truncateLastWord is false before we give
 * up and just truncate at the maxLength position, 10 by default (but not greater than maxLength)
 * @param {Boolean|String} [options.ellipsis] omission symbol for truncated string, '...' by default
 * @return {String} truncated string
 */
export default function truncate(string, maxLength, options) {
  const EMPTY_OBJECT = {};
  const EMPTY_STRING = '';
  const DEFAULT_TRUNCATE_SYMBOL = '...';
  const DEFAULT_SLOP = maxLength < 10 ? maxLength : 10;
  const EXCLUDE_TAGS = ['img', 'br']; // non-closed tags
  const items = []; // stack for saving tags
  let total = 0; // record how many characters we traced so far
  let content = EMPTY_STRING; // truncated text storage
  const KEY_VALUE_REGEX = '([\\w|-]+\\s*(=\\s*"[^"]*")?\\s*)*';
  const IS_CLOSE_REGEX = '\\s*\\/?\\s*';
  const CLOSE_REGEX = '\\s*\\/\\s*';
  const SELF_CLOSE_REGEX = new RegExp(`<\\/?\\w+\\s*${KEY_VALUE_REGEX}${CLOSE_REGEX}>`);
  const HTML_TAG_REGEX = new RegExp(`<\\/?\\w+\\s*${KEY_VALUE_REGEX}${IS_CLOSE_REGEX}>`);
  const URL_REGEX =
    // eslint-disable-next-line sonarjs/super-linear-regex, sonarjs/single-char-in-character-classes -- preserve URL matching behavior
    /(((ftp|https?):\/\/)[\w@:%_+.~#?,&/=/-]+)|((mailto:)?[_.\w-]+@([\w][\w-]+\.)+[a-zA-Z]{2,3})/g; // Simple regexp
  const IMAGE_TAG_REGEX = new RegExp(`<img\\s*${KEY_VALUE_REGEX}${IS_CLOSE_REGEX}>`);
  const WORD_BREAK_REGEX = new RegExp('\\W+', 'g');
  let matches = true;
  let result;
  let index;
  let tag;
  let selfClose;

  /**
   * Remove image tag
   *
   * @private
   * @method removeImageTag
   * @param {String} str not-yet-processed string
   * @return {String} string without image tags
   */
  function removeImageTag(str) {
    const match = IMAGE_TAG_REGEX.exec(str);
    let idx = 0;
    let len = 0;

    if (!match) {
      return str;
    }

    idx = match.index;
    len = match[0].length;

    return str.substr(0, idx) + str.substring(idx + len);
  }

  /**
   * Dump all close tags and append to truncated content while reaching upperbound
   *
   * @private
   * @method dumpCloseTag
   * @param {String[]} tags a list of tags which should be closed
   * @return {String} well-formatted html
   */
  function dumpCloseTag(tags) {
    let html = '';

    tags.reverse().forEach((tagI) => {
      // dump non-excluded tags only
      if (EXCLUDE_TAGS.indexOf(tagI) === -1) {
        html += `</${tagI}>`;
      }
    });

    return html;
  }

  /**
   * Process tag string to get pure tag name
   *
   * @private
   * @method getTag
   * @param {String} str original html
   * @return {String} tag name
   */
  function getTag(str) {
    let tail = str.indexOf(' ');

    // we have to figure out how to handle non-well-formatted HTML case
    if (tail === -1) {
      tail = str.indexOf('>');
      if (tail === -1) {
        throw new Error(`HTML tag is not well-formed : ${str}`);
      }
    }

    return string.substring(1, tail);
  }

  /**
   * Get the end position for String#substring()
   *
   * If options.truncateLastWord is FALSE, we try to the end position up to
   * options.slop characters to avoid breaking in the middle of a word.
   *
   * @private
   * @method getEndPosition
   * @param {String} str original html
   * @param {Number} tailPos (optional) provided to avoid extending the slop into trailing HTML tag
   * @return {Number} maxLength
   */
  function getEndPosition(str, tailPos) {
    const defaultPos = maxLength - total;
    let position = defaultPos;
    const isShort = defaultPos < options.slop;
    const slopPos = isShort ? defaultPos : options.slop - 1;
    let substr;
    const startSlice = isShort ? 0 : defaultPos - options.slop;
    const endSlice = tailPos || defaultPos + options.slop;
    let res;

    if (!options.truncateLastWord) {
      substr = str.slice(startSlice, endSlice);

      if (tailPos && substr.length <= tailPos) {
        position = substr.length;
      } else {
        while ((res = WORD_BREAK_REGEX.exec(substr)) !== null) {
          // a natural break position before the hard break position
          if (res.index < slopPos) {
            position = defaultPos - (slopPos - res.index);
            // keep seeking closer to the hard break position
            // unless a natural break is at position 0
            if (res.index === 0 && defaultPos <= 1) break;
          }
          // a natural break position exactly at the hard break position
          else if (res.index === slopPos) {
            position = defaultPos;
            break; // seek no more
          }
          // a natural break position after the hard break position
          else {
            position = defaultPos + (res.index - slopPos);
            break; // seek no more
          }
        }
      }
      if (str.charAt(position - 1).match(/\s$/)) position--;
    }
    return position;
  }

  options = options || EMPTY_OBJECT;
  options.ellipsis = undefined !== options.ellipsis ? options.ellipsis : DEFAULT_TRUNCATE_SYMBOL;
  options.truncateLastWord =
    undefined !== options.truncateLastWord ? options.truncateLastWord : true;
  options.slop = undefined !== options.slop ? options.slop : DEFAULT_SLOP;

  while (matches) {
    matches = HTML_TAG_REGEX.exec(string);

    if (!matches) {
      if (total >= maxLength) {
        break;
      }

      matches = URL_REGEX.exec(string);
      if (!matches || matches.index >= maxLength) {
        content += string.substring(0, getEndPosition(string));
        break;
      }

      while (matches) {
        result = matches[0];
        index = matches.index;
        content += string.substring(0, index + result.length - total);
        string = string.substring(index + result.length);
        matches = URL_REGEX.exec(string);
      }
      break;
    }

    result = matches[0];
    index = matches.index;

    if (total + index > maxLength) {
      // exceed given `maxLength`, dump everything to clear stack
      content += string.substring(0, getEndPosition(string, index));
      break;
    } else {
      total += index;
      content += string.substring(0, index);
    }

    if (result[1] === '/') {
      // move out open tag
      items.pop();
      selfClose = null;
    } else {
      selfClose = SELF_CLOSE_REGEX.exec(result);
      if (!selfClose) {
        tag = getTag(result);

        items.push(tag);
      }
    }

    if (selfClose) {
      content += selfClose[0];
    } else {
      content += result;
    }
    string = string.substring(index + result.length);
  }

  if (string.length > maxLength - total && options.ellipsis) {
    content += options.ellipsis;
  }
  content += dumpCloseTag(items);

  if (!options.keepImageTag) {
    content = removeImageTag(content);
  }

  return content;
}
